import React, { useCallback, useEffect, useMemo } from 'react';

import { Hit } from '@algolia/client-search';
import { useHistory } from 'react-router-dom';
import aa from 'search-insights';

import searchImg from 'assets/undraw_dreamer.svg';
import notFoundImg from 'assets/undraw_not_found.svg';
import LoadingContainer from 'components/LoadingContainer';
import { useSearch } from 'hooks/search';
import Service from 'interfaces/Service';
import { trackGoogleAnalyticsEvent } from 'services/google-analytics';

import Result from './Result';
import { Container, ResultsList, NoResultsContainer } from './styles';

interface SearchResultsProps {
  queryID?: string;
  searchText: string;
  hasSearched: boolean;
  loading: boolean;
  searchResults: Hit<Service>[];
}

const SearchResults: React.FC<SearchResultsProps> = ({
  queryID,
  searchText,
  hasSearched,
  loading,
  searchResults,
}) => {
  const history = useHistory();
  const { selectSearch } = useSearch();

  const orderedSearchResults = useMemo(
    () =>
      searchResults.sort((a, b) => {
        if (a.numReviews === b.numReviews) {
          if (a.cellphones.length === b.cellphones.length) {
            return b.phones.length - a.phones.length;
          }

          return b.cellphones.length - a.cellphones.length;
        }

        return a.numReviews < b.numReviews ? 1 : -1;
      }),
    [searchResults],
  );

  const onResultClick = useCallback(
    (result: Hit<Service>) => {
      selectSearch({
        searchTerm: searchText,
      });

      if (!queryID) {
        return;
      }

      // Analytics
      aa('clickedObjectIDsAfterSearch', {
        index: 'RG4Q2OFEVQ',
        eventName: 'Click service',
        queryID,
        objectIDs: [result.objectID],
        positions: [42],
      });
      trackGoogleAnalyticsEvent('Search', 'Click service', result.name);

      history.push(`/services/${result.objectID}`);
    },
    [history, queryID, searchText, selectSearch],
  );

  useEffect(() => {
    aa('init', {
      appId: 'RG4Q2OFEVQ',
      apiKey: process.env.REACT_APP_ALGOLIA_API_KEY || '',
    });
  }, []);

  return (
    <Container collapse={!hasSearched}>
      {loading ? (
        <LoadingContainer />
      ) : orderedSearchResults.length !== 0 ? (
        <ResultsList resultsNum={orderedSearchResults.length}>
          {orderedSearchResults.map((result) => (
            <Result result={result} onResultClick={onResultClick} />
          ))}
        </ResultsList>
      ) : hasSearched && searchText !== '' ? (
        <NoResultsContainer>
          <img src={notFoundImg} alt="Pesquisa sem resultados" width={220} />
          <p>Não foram encontrados resultados para a sua pesquisa</p>
        </NoResultsContainer>
      ) : (
        hasSearched && (
          <NoResultsContainer>
            <img src={searchImg} alt="Pesquisa" width={220} />
            <p>Pesquise por um serviço acima para visualizar os resultados</p>
          </NoResultsContainer>
        )
      )}
    </Container>
  );
};

export default SearchResults;
